// Function endpoints for different platforms
const NETLIFY_FUNCTION_URL = '/.netlify/functions/send-email';
const VERCEL_API_URL = '/api/send-email-resend';

// Detect deployment platform
const getEmailEndpoint = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // Local development (including Netlify Dev)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // If using Netlify Dev (port 8888), use Netlify function
    if (port === '8888') {
      return NETLIFY_FUNCTION_URL;
    }
    // Otherwise use mock service
    return null;
  }
  
  // Resin Surfaces London domain - use Vercel API (since deployed on Vercel)
  if (hostname === 'resinsurfaceslondon.co.uk' || hostname === 'www.resinsurfaceslondon.co.uk') {
    return VERCEL_API_URL;
  }
  
  // Vercel deployment (vercel.app domain)
  if (hostname.includes('vercel.app') || hostname.includes('vercel.com')) {
    return VERCEL_API_URL;
  }
  
  // Netlify deployment (netlify.app domain)
  if (hostname.includes('netlify.app') || hostname.includes('netlify.com')) {
    return NETLIFY_FUNCTION_URL;
  }
  
  // Default to Vercel API for custom domains (since this site is on Vercel)
  return VERCEL_API_URL;
};

export interface EmailData {
  name: string;
  email: string;
  phone: string;
  postcode: string;
  serviceType: string;
  message: string;
}

export const sendContactEmail = async (formData: EmailData): Promise<{ success: boolean; message: string }> => {
  try {
    // Get the appropriate endpoint for the current platform
    const emailEndpoint = getEmailEndpoint();
    
    // For local development or when no endpoint is available, simulate email sending
    if (!emailEndpoint) {
      console.log('Local Development Mode - Email would be sent with data:', formData);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return {
        success: true,
        message: 'Thank you for your enquiry! We will contact you within 24 hours. (Development Mode)'
      };
    }

    // Prepare email content
    const subject = `New Contact Form Submission from ${formData.name}`;
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #FECE1B; border-bottom: 3px solid #FECE1B; padding-bottom: 10px;">
          New Contact Form Submission - Resin Surfaces London
        </h2>
        
        <div style="background: #f8fafc; padding: 20px; border-radius: 10px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Customer Details:</h3>
          <p><strong>Name:</strong> ${formData.name}</p>
          <p><strong>Email:</strong> <a href="mailto:${formData.email}">${formData.email}</a></p>
          <p><strong>Phone:</strong> <a href="tel:${formData.phone}">${formData.phone}</a></p>
          <p><strong>Postcode:</strong> ${formData.postcode}</p>
          <p><strong>Service Required:</strong> ${formData.serviceType}</p>
        </div>
        
        ${formData.message ? `
        <div style="background: #ffffff; padding: 20px; border-left: 4px solid #FECE1B; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Project Details:</h3>
          <p style="white-space: pre-wrap;">${formData.message}</p>
        </div>
        ` : ''}
        
        <div style="background: #e2e8f0; padding: 15px; border-radius: 5px; margin: 20px 0; font-size: 14px; color: #64748b;">
          <p><strong>Next Steps:</strong></p>
          <ul>
            <li>Reply directly to this email to contact the customer</li>
            <li>Customer's email: <a href="mailto:${formData.email}">${formData.email}</a></li>
            <li>Customer's phone: <a href="tel:${formData.phone}">${formData.phone}</a></li>
          </ul>
        </div>
        
        <hr style="border: none; border-top: 2px solid #FECE1B; margin: 30px 0;">
        <p style="font-size: 12px; color: #64748b; text-align: center;">
          This email was sent from the Resin Surfaces London contact form.<br>
          Website: <a href="https://www.resinsurfaceslondon.co.uk">www.resinsurfaceslondon.co.uk</a>
        </p>
      </div>
    `;

    // Send email using the appropriate platform endpoint
    console.log('Using email endpoint:', emailEndpoint);
    const response = await fetch(emailEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: 'info@resinsurfaceslondon.co.uk',
        subject: subject,
        html: htmlContent,
        replyTo: formData.email
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Email endpoint error:', response.status, errorText);
      throw new Error(`Email endpoint returned ${response.status}: ${errorText}`);
    }

    const result = await response.json();

    if (result.success) {
      console.log('Email sent successfully:', result);
      return {
        success: true,
        message: 'Thank you for your enquiry! We will contact you within 24 hours.'
      };
    } else {
      throw new Error(result.error || 'Failed to send email');
    }

  } catch (error) {
    console.error('Error sending email:', error);
    return {
      success: false,
      message: 'Sorry, there was an error sending your message. Please try again or contact us directly at info@resinsurfaceslondon.co.uk'
    };
  }
};

